"use client"

import React from 'react';
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { NavigationProps } from "@/types/navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { MailIcon, MessageCircleIcon, PhoneIcon } from "lucide-react";
import { PageHeader } from "@/components/ui/page-header";

interface HelpPageProps extends NavigationProps {}

export default function HelpPage({ onNavigate }: HelpPageProps) {
  const faqs = [
    {
      question: "How do I add a new transaction?",
      answer: "To add a new transaction, navigate to the Transactions page and click the 'Add Transaction' button in the top right corner. Fill in the required details and click 'Save'."
    },
    {
      question: "How do I link my bank account?",
      answer: "You can link your bank account by going to the Cards page and selecting 'Link New Account'. Follow the secure authentication process to connect your account."
    },
    {
      question: "How do I set up budget categories?",
      answer: "Budget categories can be created and managed from the Dashboard. Click on 'Manage Categories' and follow the prompts to create new categories or edit existing ones."
    },
    {
      question: "How can I export my transaction data?",
      answer: "You can export your transaction data by going to the Transactions page, selecting the date range, and clicking the 'Export' button. You can choose between CSV, PDF, or Excel formats."
    },
    {
      question: "How do I set up notifications?",
      answer: "Notification settings can be configured from the Notifications page. Click on 'Settings' to customize which alerts you want to receive and how you want to receive them."
    },
  ];

  return (
    <>
      <PageHeader 
        onNavigate={onNavigate}
        currentPage="Help & Support"
        showDashboardLink={false}
      />

      <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
        <div className="mb-4 md:mb-6">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Help & Support</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Find answers to common questions or contact our support team</p>
        </div>

        <div className="grid gap-4 md:gap-6 md:grid-cols-1 lg:grid-cols-2">
          {/* Contact card - Full width on small/medium screens, half width on large */}
          <Card className="w-full">
            <CardHeader className="px-4 py-3 sm:px-6 sm:py-4">
              <CardTitle className="text-lg">Contact Support</CardTitle>
              <CardDescription className="text-sm">
                Get in touch with our support team for personalized assistance
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3 sm:gap-4 px-4 pb-4 sm:px-6">
              <div className="flex items-center gap-3 rounded-lg border p-3 sm:p-4">
                <MailIcon className="h-5 w-5 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">Email Support</p>
                  <p className="text-xs sm:text-sm text-muted-foreground truncate">support@personalbudget.com</p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="ml-auto shrink-0"
                >
                  Email
                </Button>
              </div>
              
              <div className="flex items-center gap-3 rounded-lg border p-3 sm:p-4">
                <PhoneIcon className="h-5 w-5 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">Phone Support</p>
                  <p className="text-xs sm:text-sm text-muted-foreground truncate">Available Mon-Fri, 9am-5pm EST</p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="ml-auto shrink-0"
                >
                  Call
                </Button>
              </div>
              
              <div className="flex items-center gap-3 rounded-lg border p-3 sm:p-4">
                <MessageCircleIcon className="h-5 w-5 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">Live Chat</p>
                  <p className="text-xs sm:text-sm text-muted-foreground truncate">Chat with our support team</p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="ml-auto shrink-0"
                >
                  Chat
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* FAQ card - Full width on small/medium screens, half width on large */}
          <Card className="w-full">
            <CardHeader className="px-4 py-3 sm:px-6 sm:py-4">
              <CardTitle className="text-lg">Frequently Asked Questions</CardTitle>
              <CardDescription className="text-sm">
                Quick answers to common questions about using Personal Budget
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4 sm:px-6">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-sm py-3">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="text-xs sm:text-sm text-muted-foreground pb-2">{faq.answer}</p>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>

          {/* Video Tutorials - Full width across both columns on larger screens */}
          <Card className="w-full lg:col-span-2">
            <CardHeader className="px-4 py-3 sm:px-6 sm:py-4">
              <CardTitle className="text-lg">Video Tutorials</CardTitle>
              <CardDescription className="text-sm">
                Learn how to use Personal Budget with our step-by-step video guides
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4 sm:px-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                <div className="rounded-lg border p-2 sm:p-3">
                  <div className="aspect-video bg-muted rounded-md mb-2 flex items-center justify-center text-muted-foreground text-xs">
                    Video Preview
                  </div>
                  <h3 className="font-medium text-xs sm:text-sm">Getting Started Guide</h3>
                  <p className="text-xs text-muted-foreground mt-1">Learn the basics of Personal Budget</p>
                </div>
                <div className="rounded-lg border p-2 sm:p-3">
                  <div className="aspect-video bg-muted rounded-md mb-2 flex items-center justify-center text-muted-foreground text-xs">
                    Video Preview
                  </div>
                  <h3 className="font-medium text-xs sm:text-sm">Setting Up Categories</h3>
                  <p className="text-xs text-muted-foreground mt-1">Create and manage budget categories</p>
                </div>
                <div className="rounded-lg border p-2 sm:p-3">
                  <div className="aspect-video bg-muted rounded-md mb-2 flex items-center justify-center text-muted-foreground text-xs">
                    Video Preview
                  </div>
                  <h3 className="font-medium text-xs sm:text-sm">Advanced Tracking</h3>
                  <p className="text-xs text-muted-foreground mt-1">Tips for better financial management</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </>
  );
} 